/**
 * RELEASED: 2025-07-01 at 22:30
 */
import React, { useEffect, useState } from 'react';
import Head from 'src/components/head';
import { Provider as ReduxProvider } from 'react-redux';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Remove if locales are not used
import 'src/locales/i18n';

import { RTL } from 'src/components/rtl';
import SplashScreen from 'src/components/ui/splash-screen';
import { SettingsButton } from 'src/components/settings/settings-button';
import { SettingsDrawer } from 'src/components/settings/settings-drawer';
import { Toaster } from 'src/components/toaster';
// import { gtmConfig } from 'src/config';
import { AuthConsumer, AuthProvider } from 'src/contexts/auth/jwt';
import { SettingsConsumer, SettingsProvider } from 'src/contexts/settings';
// import { useAnalytics } from 'src/hooks/use-analytics';
// import { useNprogress } from 'src/hooks/use-nprogress';
import { store } from 'src/store';
import { createTheme } from 'src/theme';
import { createEmotionCache } from 'src/utils/create-emotion-cache';

import AppRouter from 'src/routes/app-router';
// import { showLog } from 'src/utils/debug-log';
import { initializeCache } from 'src/utils/data-cache';

const clientSideEmotionCache = createEmotionCache();
function App(props) {
  const { emotionCache = clientSideEmotionCache } = props;

  // useAnalytics(gtmConfig);
  // const { isLoading } = useNprogress();

  const [isCacheReady, setIsCacheReady] = useState(false);
  useEffect(() => {
    //showLog('*****===== START =====*****');
    // console.trace(); // Add this line to log the stack trace
    const fetchData = async () => {
      try {
        await initializeCache(); // Preload all data into the cache
      } catch (error) {
        console.error('Failed to initialize cache:', error);
      }finally{
        setIsCacheReady(true);   // Mark cache as ready
      }
    };
    fetchData();

    // return () => {
    //   //showLog('*****===== END =====*****');
    // };
  }, []);
  //showLog('********** APP **********');
  // Display a loading indicator until cache is ready
  if (!isCacheReady) {
    return <SplashScreen />;
  }

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>TC439</title>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>
      <ReduxProvider store={store}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AuthProvider>
            <AuthConsumer>
              {(auth) => (
                <SettingsProvider>
                  <SettingsConsumer>
                    {(settings) => {
                      const theme = createTheme({
                        colorPreset: settings.colorPreset,
                        contrast: settings.contrast,
                        direction: settings.direction,
                        paletteMode: settings.paletteMode,
                        responsiveFontSizes: settings.responsiveFontSizes,
                      });
                      // Prevent guards from redirecting
                      const showSlashScreen = !auth.isInitialized;
                      const loggedIn = auth.isAuthenticated;

                      return (
                        <ThemeProvider theme={theme}>
                          <Head>
                            <meta
                              name="color-scheme"
                              content={settings.paletteMode}
                            />
                            <meta
                              name="theme-color"
                              content={theme.palette.neutral[900]}
                            />
                          </Head>
                          <RTL direction={settings.direction}>
                            <CssBaseline />
                            {showSlashScreen ? (
                              <SplashScreen />
                            ) : (
                              <>
                                <AppRouter isauthed={loggedIn} />
                                {!loggedIn && 
                                  <SettingsButton
                                    onClick={settings.handleDrawerOpen}
                                  />
                                }
                                <SettingsDrawer
                                  canReset={settings.isCustom}
                                  onClose={settings.handleDrawerClose}
                                  onReset={settings.handleReset}
                                  onUpdate={settings.handleUpdate}
                                  open={settings.openDrawer}
                                  values={{
                                    colorPreset: settings.colorPreset,
                                    contrast: settings.contrast,
                                    direction: settings.direction,
                                    paletteMode: settings.paletteMode,
                                    responsiveFontSizes:
                                      settings.responsiveFontSizes,
                                    stretch: settings.stretch,
                                    layout: settings.layout,
                                    navColor: settings.navColor,
                                  }}
                                />
                              </>
                            )}
                            <Toaster />
                          </RTL>
                        </ThemeProvider>
                      );
                    }}
                  </SettingsConsumer>
                </SettingsProvider>
              )}
            </AuthConsumer>
          </AuthProvider>
        </LocalizationProvider>
      </ReduxProvider>
    </CacheProvider>
  );
}

export default App;
